import React, { useState, useEffect, useRef } from 'react';
import { X, CheckCircle, ClipboardCheck, Upload, Calendar, RefreshCw } from 'lucide-react';

const STORAGE_KEY = 'dcb_guide_dismissed_until';
const VISIT_COUNT_KEY = 'dcb_visit_count';

function shouldShowModal() {
  // Session check — already dismissed this browser session?
  if (sessionStorage.getItem('dcb_guide_seen')) return false;

  // Check localStorage — dismissed until a future date?
  const dismissedUntil = localStorage.getItem(STORAGE_KEY);
  if (dismissedUntil) {
    const now = new Date().toISOString().slice(0, 10);
    if (now <= dismissedUntil) return false;
  }

  // Visit count logic: show on 1st visit, then every 3rd visit
  const count = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0', 10) + 1;
  localStorage.setItem(VISIT_COUNT_KEY, String(count));

  // 1st visit = show, then show on 3rd, 6th, 9th... (every 3 visits)
  if (count === 1) return true;
  if (count % 3 === 0) return true;

  return false;
}

function dismissModal(forToday) {
  // Always mark as seen for this session
  sessionStorage.setItem('dcb_guide_seen', 'true');

  if (forToday) {
    // Don't show until tomorrow
    const today = new Date().toISOString().slice(0, 10);
    localStorage.setItem(STORAGE_KEY, today);
  }
}

const GuideModal = () => {
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const checkedRef = useRef(false);

  useEffect(() => {
    // Only check once per mount (not on re-renders)
    if (checkedRef.current) return;
    checkedRef.current = true;

    if (shouldShowModal()) {
      const timer = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setClosing(true);
    dismissModal(dontShowAgain);
    setTimeout(() => { setVisible(false); setClosing(false); }, 250);
  };

  const handleCheckin = () => {
    dismissModal(dontShowAgain);
    setClosing(true);
    setTimeout(() => { setVisible(false); setClosing(false); }, 250);
    const el = document.querySelector('[data-testid="checkin-trigger"]');
    if (el) el.click();
  };

  if (!visible) return null;

  const items = [
    { icon: Calendar, text: 'Günlük check-in yap' },
    { icon: CheckCircle, text: 'Görevlerini tamamla' },
    { icon: Upload, text: 'Belgelerini yükle' },
    { icon: RefreshCw, text: 'Test zamanı geldiğinde değerlendirmeni güncelle' },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        background: 'rgba(0,0,0,0.45)',
        animation: closing ? 'gmFadeOut 0.25s ease forwards' : 'gmFadeIn 0.3s ease forwards',
      }}
      data-testid="guide-modal-backdrop"
    >
      <style>{`
        @keyframes gmFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes gmFadeOut { from { opacity: 1; } to { opacity: 0; } }
        @keyframes gmScaleIn { from { opacity: 0; transform: scale(0.95) translateY(12px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        @keyframes gmScaleOut { from { opacity: 1; transform: scale(1); } to { opacity: 0; transform: scale(0.95) translateY(8px); } }
        @keyframes gmItemIn { from { opacity: 0; transform: translateX(-8px); } to { opacity: 1; transform: translateX(0); } }
      `}</style>

      <div
        className="relative max-w-md w-full"
        style={{
          background: 'rgba(10, 18, 38, 0.92)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '16px',
          padding: '28px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 30px rgba(255,200,100,0.12)',
          animation: closing ? 'gmScaleOut 0.25s ease forwards' : 'gmScaleIn 0.35s cubic-bezier(0.16,1,0.3,1) forwards',
        }}
        data-testid="guide-modal-box"
      >
        <button onClick={handleClose} className="absolute top-4 right-4 p-1 rounded-lg transition-colors hover:bg-white/10" data-testid="guide-modal-close">
          <X className="w-5 h-5" style={{ color: 'rgba(255,255,255,0.4)' }} />
        </button>

        <h3 style={{ fontSize: '19px', fontWeight: 700, color: '#F1F5F9', lineHeight: 1.5, marginBottom: '14px', paddingRight: '28px' }} data-testid="guide-modal-title">
          Seni daha doğru anlayabilmemiz için küçük güncellemeler önemli
        </h3>

        <div style={{ fontSize: '14px', lineHeight: 1.75, color: 'rgba(203,213,225,0.85)', marginBottom: '22px' }}>
          <p>Bu sistem seninle birlikte öğrenir.</p>
          <p className="mt-2">Her gün kısa bir check-in yapman, görevlerini tamamlaman ve yeni sağlık belgelerini eklemen çok önemli.</p>
          <p className="mt-2">Yeni bir hastane raporu aldığında sisteme yüklemen, sana verilen önerilerin ve değerlendirmelerin daha doğru olmasını sağlar.</p>
        </div>

        <div className="space-y-2.5 mb-6">
          {items.map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.05)', animation: `gmItemIn 0.3s ease ${0.15 + i * 0.08}s both` }}
                data-testid={`guide-item-${i}`}>
                <div className="flex items-center justify-center shrink-0" style={{ width: '30px', height: '30px', borderRadius: '8px', background: 'rgba(250,204,21,0.1)' }}>
                  <Icon className="w-4 h-4" style={{ color: '#FACC15' }} />
                </div>
                <span style={{ fontSize: '14px', fontWeight: 500, color: '#E2E8F0' }}>{item.text}</span>
              </div>
            );
          })}
        </div>

        <div className="flex gap-3 mb-4">
          <button onClick={handleClose} className="flex-1 py-3 rounded-xl font-semibold text-sm transition-all hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg, #D97706, #F59E0B)', color: '#0F172A', boxShadow: '0 6px 20px rgba(245,158,11,0.25)' }}
            data-testid="guide-modal-understood">
            Anladım
          </button>
          <button onClick={handleCheckin} className="flex-1 py-3 rounded-xl font-semibold text-sm transition-all hover:-translate-y-0.5"
            style={{ background: 'transparent', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.3)' }}
            data-testid="guide-modal-checkin">
            <ClipboardCheck className="w-4 h-4 inline mr-1.5" style={{ marginTop: '-1px' }} />
            Check-in Yap
          </button>
        </div>

        <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="guide-modal-checkbox">
          <input type="checkbox" checked={dontShowAgain} onChange={(e) => setDontShowAgain(e.target.checked)} className="rounded" style={{ accentColor: '#F59E0B' }} />
          <span style={{ fontSize: '12px', color: 'rgba(148,163,184,0.7)' }}>Bugün tekrar gösterme</span>
        </label>
      </div>
    </div>
  );
};

export default GuideModal;
